import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/api";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import Reveal from "@/components/Reveal";

const EXPECTATIONS = [
  "Flexibility", "Online accessibility", "Personalisation", "High-quality educators",
  "Structured curricula", "Measurable progress", "Technology-enabled learning",
];

export default function About() {
  const [company, setCompany] = useState(null);
  useEffect(() => {
    api.get("/company").then((r) => setCompany(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const creds = [
    ["Legal Name", company?.legalName],
    ["CIN", company?.cin],
    ["Incorporation Date", company?.incorporationDate],
    ["Company Type", company?.companyType],
    ["Registrar of Companies", company?.roc],
    ["Status", company?.status],
  ].filter(([, v]) => v);

  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About RCS"
        title="Roy Career Solutions Private Limited"
        lede={company?.positioning || "Building Transformative Learning Experiences for the Next Generation"}
      />

      <section className="py-24 lg:py-32" data-testid="our-story">
        <div className="container-rcs grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Section eyebrow="Our Story" title="Specialised brands, not one generic platform." />
            <Reveal delay={0.1}>
              <div className="mt-6 space-y-5 text-base leading-relaxed text-slate-600">
                <p>
                  RCS was incorporated in 2024 with the ambition of creating specialised education
                  businesses serving different learner segments. The organisation recognised that
                  learners increasingly expect more from education than a single platform can offer.
                </p>
                <p>
                  RCS therefore develops specialised brands rather than attempting to deliver every
                  type of education through one generic platform. This philosophy has resulted in
                  distinct brands serving different educational needs — academic education through
                  The Topper Academy, and music education through Indian School of Music.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-2.5" data-testid="learner-expectations">
                {EXPECTATIONS.map((e) => (
                  <span key={e} className="rounded-full border border-navy-900/15 bg-white px-4 py-1.5 text-xs font-semibold text-navy-900">
                    {e}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-5">
            <div className="rounded-2xl bg-navy-950 p-8 lg:p-10" data-testid="credentials-card">
              <p className="eyebrow-light">Corporate Credentials</p>
              <dl className="mt-6 space-y-5">
                {creds.map(([k, v]) => (
                  <div key={k} className="flex flex-col gap-1 border-b border-white/10 pb-4 last:border-0 last:pb-0">
                    <dt className="text-[11px] uppercase tracking-[0.2em] text-slate-500">{k}</dt>
                    <dd className="text-sm font-semibold text-ivory">{v}</dd>
                  </div>
                ))}
              </dl>
              <Link to="/corporate-information" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-gold hover:text-gold-light transition-colors" data-testid="about-corporate-info-cta">
                Full Corporate Information <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-navy-900/10 bg-white py-24" data-testid="about-philosophy">
        <div className="container-rcs">
          <Section
            eyebrow="Our Philosophy"
            title="Education experiences designed for today's learners."
            lede="The organisation combines expert educators, structured curricula, technology, learner-centric methodologies, personalised learning, digital accessibility and operational excellence to create education experiences designed for today's learners."
          />
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/purpose" className="btn-gold" data-testid="about-purpose-cta">
                Why We Exist <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
              <Link to="/brands" className="btn-outline-dark" data-testid="about-brands-cta">
                Explore Our Brands
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
